import "./Pagination.css";

// Builds a compact window of page numbers (max 7 visible) around the
// current page, with leading/trailing "jump to first/last" + ellipsis
// when the total page count is larger than the window.
function getPageWindow(currentPage, totalPages) {
  const maxButtons = 7;
  if (totalPages <= maxButtons) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  let start = Math.max(1, currentPage - 3);
  let end = start + maxButtons - 1;
  if (end > totalPages) {
    end = totalPages;
    start = end - maxButtons + 1;
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

/**
 * Purely presentational. Renders nothing when `showPagination` is false
 * (i.e. the caller's usePagination() determined the data fits on one
 * page). All business logic — current page, totals, slicing — lives in
 * usePagination; this component only ever calls the functions it's given.
 *
 * Usage: <Pagination {...paginationResult} />
 * where paginationResult is the object returned by usePagination().
 */
function Pagination({
  currentPage,
  totalPages,
  totalRecords,
  startIndex,
  endIndex,
  hasPrev,
  hasNext,
  goToPage,
  nextPage,
  prevPage,
  showPagination,
}) {
  if (!showPagination) return null;

  const pageWindow = getPageWindow(currentPage, totalPages);
  const showLeadingGap = pageWindow[0] > 1;
  const showTrailingGap = pageWindow[pageWindow.length - 1] < totalPages;

  return (
    <div className="pagination-bar">
      <span className="pagination-info">
        Showing {startIndex}–{endIndex} of {totalRecords} entries
      </span>

      <div className="pagination-controls">
        <button
          type="button"
          className="pagination-btn pagination-nav"
          disabled={!hasPrev}
          onClick={prevPage}
        >
          ← Previous
        </button>

        <div className="pagination-pages">
          {showLeadingGap && (
            <>
              <button type="button" className="pagination-btn" onClick={() => goToPage(1)}>
                1
              </button>
              {pageWindow[0] > 2 && <span className="pagination-ellipsis">…</span>}
            </>
          )}

          {pageWindow.map((page) => (
            <button
              type="button"
              key={page}
              className={`pagination-btn${page === currentPage ? " active" : ""}`}
              onClick={() => goToPage(page)}
            >
              {page}
            </button>
          ))}

          {showTrailingGap && (
            <>
              {pageWindow[pageWindow.length - 1] < totalPages - 1 && (
                <span className="pagination-ellipsis">…</span>
              )}
              <button type="button" className="pagination-btn" onClick={() => goToPage(totalPages)}>
                {totalPages}
              </button>
            </>
          )}
        </div>

        <button
          type="button"
          className="pagination-btn pagination-nav"
          disabled={!hasNext}
          onClick={nextPage}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

export default Pagination;